import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import DownloadOutlined from '@mui/icons-material/DownloadOutlined'
import DriveFileMoveOutlined from '@mui/icons-material/DriveFileMoveOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import ImageOutlined from '@mui/icons-material/ImageOutlined'
import MoreVert from '@mui/icons-material/MoreVert'
import TableChart from '@mui/icons-material/TableChart'
import VideocamOutlined from '@mui/icons-material/VideocamOutlined'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FileRepositoryModel } from 'src/models/file-repository-model'
import { formatFileSize, formatShortDate } from '../utils/fileHelpers'

interface FileCardProps {
  entry: FileRepositoryModel
  childCount?: number
  onDirectoryClick: (entry: FileRepositoryModel) => void
  onFileClick: (entry: FileRepositoryModel) => void
  onRenameClick: (entry: FileRepositoryModel) => void
  onMoveClick: (entry: FileRepositoryModel) => void
  onDeleteClick: (entry: FileRepositoryModel) => void
  onDownload: (entry: FileRepositoryModel) => void
  canUpdate: boolean
  canDelete: boolean
  canDownload: boolean
}

const getFileIcon = (entry: FileRepositoryModel, childCount?: number) => {
  if (entry.type === 'DIRECTORY') {
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <FolderOutlined sx={{ fontSize: 48, color: '#FF5E1E' }} />
        {childCount !== undefined && (
          <Box
            sx={{
              position: 'absolute',
              top: -6,
              right: -10,
              backgroundColor: '#FF5E1E',
              color: '#fff',
              borderRadius: '10px',
              minWidth: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '5px',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {childCount}
          </Box>
        )}
      </Box>
    )
  }
  if (entry.mimeType?.startsWith('image/')) {
    return <ImageOutlined sx={{ fontSize: 48, color: 'rgba(0,0,0,0.4)' }} />
  }
  if (entry.mimeType === 'video/mp4') {
    return <VideocamOutlined sx={{ fontSize: 48, color: 'rgba(0,0,0,0.4)' }} />
  }
  if (entry.mimeType === 'text/csv') {
    return <TableChart sx={{ fontSize: 48, color: 'rgba(0,0,0,0.4)' }} />
  }
  return <ImageOutlined sx={{ fontSize: 48, color: 'rgba(0,0,0,0.4)' }} />
}

export function FileCard({
  entry,
  childCount,
  onDirectoryClick,
  onFileClick,
  onRenameClick,
  onMoveClick,
  onDeleteClick,
  onDownload,
  canUpdate,
  canDelete,
  canDownload,
}: FileCardProps) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const handleMenuOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleClick = () => {
    if (entry.type === 'DIRECTORY') {
      onDirectoryClick(entry)
    } else {
      onFileClick(entry)
    }
  }

  const isImage = entry.mimeType?.startsWith('image/')
  const hasContextMenu = canUpdate || canDelete || canDownload

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: '8px',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        ':hover': {
          borderColor: '#FF5E1E',
          boxShadow: '0 2px 8px rgba(255, 94, 30, 0.15)',
        },
      }}
    >
      <CardActionArea onClick={handleClick}>
        {/* Thumbnail / Icon area */}
        <Box
          sx={{
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            overflow: 'hidden',
          }}
        >
          {entry.type === 'FILE' && isImage && entry.url ? (
            <img
              src={entry.url}
              alt={entry.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            getFileIcon(entry, childCount)
          )}
        </Box>
      </CardActionArea>

      {/* Footer: name + context menu */}
      <CardContent
        sx={{
          p: '8px 12px !important',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ overflow: 'hidden', flex: 1, mr: 1 }}>
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={entry.name}
          >
            {entry.name}
          </Typography>
          {entry.type === 'FILE' && (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(entry.sizeInBytes)}
              {' · '}
              {formatShortDate(entry.createdAt)}
            </Typography>
          )}
          {entry.type === 'DIRECTORY' && childCount !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t('fileRepository.directory.itemCount', { count: childCount })}
            </Typography>
          )}
        </Box>

        {hasContextMenu && (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                mt: '-2px',
                color: 'rgba(0,0,0,0.4)',
                ':hover': { color: '#FF5E1E' },
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {canUpdate && (
                <MenuItem
                  onClick={() => {
                    handleMenuClose()
                    onRenameClick(entry)
                  }}
                >
                  <ListItemIcon>
                    <EditOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('fileRepository.context.rename')}</ListItemText>
                </MenuItem>
              )}
              {canUpdate && (
                <MenuItem
                  onClick={() => {
                    handleMenuClose()
                    onMoveClick(entry)
                  }}
                >
                  <ListItemIcon>
                    <DriveFileMoveOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('fileRepository.context.move')}</ListItemText>
                </MenuItem>
              )}
              {canDownload && entry.type === 'FILE' && (
                <MenuItem
                  onClick={() => {
                    handleMenuClose()
                    onDownload(entry)
                  }}
                >
                  <ListItemIcon>
                    <DownloadOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('fileRepository.context.download')}</ListItemText>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  onClick={() => {
                    handleMenuClose()
                    onDeleteClick(entry)
                  }}
                >
                  <ListItemIcon>
                    <DeleteOutlined fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>
                    {t('fileRepository.context.delete')}
                  </ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </CardContent>
    </Card>
  )
}
